import React from 'react'

import Bugsnag from '@bugsnag/js'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { InstrumentVerificationBlocker } from '@cash-web/protos/squareup/franklin/blockers/instrument_verification_blocker.pb'
import { CashInstrumentType } from '@cash-web/protos/squareup/franklin/cash_instrument_type.pb'
import { InstrumentVerificationBlockerSupplement } from '@cash-web/protos/squareup/franklin/scenarios.pb'
import { useBlockerResponseHandler } from '@cash-web/shared-blocker-data-access-context'
import { useMutationVerifyInstrument } from '@cash-web/shared-data-access-api/react-query/useMutationVerifyInstrument'
import { Region, useRegion } from '@cash-web/shared-data-access-region-provider'
import { CardNumber } from '@cash-web/shared-feature-form'
import { AccountNumber, RoutingNumber } from '@cash-web/shared-feature-form'
import { useFlowContext } from '@cash-web/shared-flow-data-access-context'

import { BaseBlocker } from '../../BaseBlocker/BaseBlocker'

export type BlockerInstrumentVerificationProps = {
  blocker: InstrumentVerificationBlocker
  supplement?: InstrumentVerificationBlockerSupplement
}

// 'US' and 'GB' are the only currently supported regions
// because we're not sure what to show for RoutingNumber or AccountNumber
// for other regions.
const supportedRegions = ['US', 'GB'] as const
type SupportedRegion = (typeof supportedRegions)[number]
function isSupportedRegion(region: Region): region is SupportedRegion {
  return supportedRegions.includes(region as SupportedRegion)
}

type InstrumentVerificationForm = {
  unencrypted_pan?: string
  routing_number_1?: string
  routing_number_2?: string
  account_number?: string
}

export const BlockerInstrumentVerification: React.FC<BlockerInstrumentVerificationProps> = ({
  blocker,
  supplement,
}) => {
  const form = useForm({ mode: 'onTouched' })
  const { t } = useTranslation()
  const flow = useFlowContext()
  const { mutateAsync: verifyInstrument } = useMutationVerifyInstrument({
    options: {
      clientScenario: flow.active?.clientScenario,
      flowToken: flow.active?.flowToken,
    },
  })
  const { handleBlockerResponse, genericError } = useBlockerResponseHandler()
  const region = useRegion()

  if (region && !isSupportedRegion(region)) {
    Bugsnag.notify({ name: 'BlockerInstrumentVerification', message: `Unexpected region ${region}` })
    throw Error(`BlockerInstrumentVerification: unexpected region ${region}`)
  }

  const handleSubmit = async (data: InstrumentVerificationForm) => {
    // NOTE: Once this blocker supports CA, we'll need to change the logic to what's commented out below
    // const routingNumber = region === 'CA' ? `0${data.routing_number_1}${data.routing_number_2}` : data.routing_number_1
    const routingNumber = data.routing_number_1

    const dataToSendToServer = {
      unencrypted_pan: data.unencrypted_pan,
      routing_number: routingNumber,
      account_number: data.account_number,
      transfer_token: flow.active?.transferToken,
      payment_tokens: flow.active?.paymentTokens,
    }
    await handleBlockerResponse({
      promisedBlockerUpdate: verifyInstrument(dataToSendToServer),
      errors: {
        CONCURRENT_MODIFICATION: genericError,
        INVALID: t("That doesn't match the info we have on file for this account. Please try again.", {
          context: 'Error to the user when the response status is INVALID',
        }),
        FAILURE: t("That doesn't match the info we have on file for this account.", {
          context: 'Error to the user when the response status is FAILURE',
        }),
        TOO_MANY_ATTEMPTS: t('There have been too many attempts to verify this information.', {
          context: 'Error to the user when the response status is TOO_MANY_ATTEMPTS',
        }),
      },
    })
  }

  if (blocker.instrument_type == null) {
    throw new Error('BlockerInstrumentVerification: instrument_type is null')
  }

  const [type] = blocker.instrument_type

  const showCardForm = type === CashInstrumentType.DEBIT_CARD || type === CashInstrumentType.CREDIT_CARD
  const showBankAccountForm = type === CashInstrumentType.BANK_ACCOUNT
  const helpModal = supplement?.additional_help_items
    ? {
        helpItems: supplement.additional_help_items,
        title: t('How can we help?', { context: 'Title text.' }),
      }
    : undefined

  return (
    <>
      {showCardForm && (
        // Note: We aren't hardcoding the title for this like in other places because we want the title to have information
        // about the card digits in it and we don't have access to that on the frontend.
        <BaseBlocker helpModal={helpModal} form={form} mainText={supplement?.main_text || ''} onSubmit={handleSubmit}>
          <CardNumber autoFocus name="unencrypted_pan" type={type} />
        </BaseBlocker>
      )}
      {showBankAccountForm && region ? (
        // Note: The `supplement?.main_text` is not being used in the title so that the web
        // can have more control of the title and subtitle.
        <BaseBlocker
          form={form}
          mainText={t('Enter your linked bank account info', { context: 'Title text.' })}
          onSubmit={handleSubmit}
          helpModal={helpModal}
        >
          <RoutingNumber autoFocus region={region} name="routing_number" />
          <AccountNumber region={region} name="account_number" />
        </BaseBlocker>
      ) : null}
    </>
  )
}
